import { useEffect, useState } from "react";
import styled from "styled-components";

import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/build/pdf.worker.entry";

// components
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  Select,
  Tooltip,
  Upload,
  message,
} from "antd";

// icons
import { InboxOutlined, QuestionCircleTwoTone } from "@ant-design/icons";

// utils
import { useAxios } from "../hooks";
import { colorPalette } from "../theme";
import AttributesDefinitionModal from "./AttributesDefinition";
import { DEFAULT_S3_FILE_BUCKET_URL } from "../constant";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const Wrapper = styled.div`
  // display: flex;
  // flex-direction: row;
  // gap: 130px;
  width: 90%;
  margin: 0px auto;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 30px;

  h3 {
    font-size: 22px;
    margin: 20px 0px 15px 0px;
  }

  p {
    margin: 0px;
    text-align: center;
  }
`;

const LabelText = styled.p`
  margin: 0px;
  font-weight: 600;
  font-size: 18px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledCard = styled(Card)`
  width: 100%;
  margin-bottom: 20px;

  .ant-upload-list-item-done {
    .anticon-paper-clip {
      svg {
        color: ${colorPalette.primary[500]};
      }
    }
    .ant-upload-list-item-name {
      color: ${colorPalette.primary[500]};
    }

    .ant-upload-list-item-action {
      .anticon-delete svg {
        color: ${colorPalette.danger[500]};
      }
    }
  }

  small {
    margin-bottom: 10px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 20px 0px;

  .ant-btn {
    width: 25%;
  }
`;

const CompanyInfo = ({ onboarding, onChangeStep, onUpdateOnboarding }) => {
  const { fetch: onFetch, loading: attributeLoading } = useAxios();
  const { fetch: onSubmit, loading: submitLoading } = useAxios();
  const [messageApi, contextHolder] = message.useMessage();

  const [attributes, setAttributes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [policyFile, setPolicyFile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [employeeFile, setEmployeeFile] = useState(null);
  const [slugName, setSlugName] = useState("");

  useEffect(() => {
    fetchAttributes();
    const domain = onboarding?.email.split("@")[1];
    setSlugName(String(domain.split(".")[0]).toLowerCase());
    setSelectedAttributes(onboarding?.attributes || []);
    setEmployeeFile(
      onboarding?.employee_file
        ? {
            uid: "-1",
            status: "done",
            name: onboarding.employee_file.split("/").slice(-1)[0],
            url: `${DEFAULT_S3_FILE_BUCKET_URL}/${onboarding.employee_file}`,
          }
        : null,
    );
    setPolicyFile(
      onboarding?.policy_file
        ? {
            uid: "-1",
            status: "done",
            name: onboarding.policy_file.split("/").slice(-1)[0],
            url: `${DEFAULT_S3_FILE_BUCKET_URL}/${onboarding.policy_file}`,
          }
        : null,
    );
    setLogo(
      onboarding?.company?.logo
        ? {
            uid: "-1",
            status: "done",
            name: onboarding?.company?.logo.split("/").slice(-1)[0],
            url: `${DEFAULT_S3_FILE_BUCKET_URL}/${onboarding?.company?.logo}`,
          }
        : null,
    );
  }, []);

  const fetchAttributes = async () => {
    try {
      const { data } = await onFetch({
        url: "/config/attributes/",
        method: "GET",
      });
      setAttributes(data || []);
    } catch (error) {
      console.error("Error fetching attributes: ", error);
      setAttributes([]);
    }
  };

  const beforeEmployeeUpload = (file) => {
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel", // .xls
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error("You can only upload CSV or Excel files!");
      return Upload.LIST_IGNORE; // stops upload
    }
    // Add file to list manually (if you're not uploading to a server)
    file.status = "done"; // Mark file as uploaded
    setEmployeeFile(file);
    return false; // prevent auto upload
  };

  const handleFileChange = async (file) => {
    // console.log("handleFileChange calling", file);
    // const file = event.target.files[0];
    // console.log(file);
    if (!file) return;

    // console.log(file.type);
    const allowedTypes = [
      "application/pdf",
      // "application/msword", // .doc
      // "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      // "text/plain", // .txt
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error("You can only upload PDF file!");
      return Upload.LIST_IGNORE; // stops upload
    }

    // Optional: limit size (e.g., 5MB)
    if (file.size / 1024 / 1024 > 5) {
      message.error("File must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }

    file.status = "done"; // Mark file as uploaded
    setPolicyFile(file);

    return false; // prevent auto upload
  };

  const handleLogoChange = async (file) => {
    // console.log(file);
    if (!file) return;

    const isImg = ["image/png", "image/jpeg"].includes(file.type);
    if (!isImg) {
      messageApi.error(`${file.name} is not a image file`);
      return Upload.LIST_IGNORE;
    }
    const isLt2MB = file.size / 1024 / 1024 < 2;
    if (!isLt2MB) {
      messageApi.error(`${file.name} must be smaller than 2MB`);
      return Upload.LIST_IGNORE;
    }

    file.status = "done"; // Mark file as uploaded
    setLogo(file);

    return false; // prevent auto upload
  };

  const onFinish = async () => {
    try {
      const formData = new FormData();
      formData.append("id", onboarding.id);
      if (policyFile) {
        if (
          policyFile?.url &&
          policyFile.url.startsWith(DEFAULT_S3_FILE_BUCKET_URL)
        ) {
          formData.append(
            "policy_file_url",
            policyFile.url.replace(`${DEFAULT_S3_FILE_BUCKET_URL}/`, ""),
          );
        } else {
          formData.append("policy_file", policyFile);
        }
      }
      if (logo) {
        if (logo?.url && logo?.url?.startsWith(DEFAULT_S3_FILE_BUCKET_URL)) {
          formData.append(
            "logo_url",
            logo.url.replace(`${DEFAULT_S3_FILE_BUCKET_URL}/`, ""),
          );
        } else {
          formData.append("logo", logo);
        }
      }
      if (employeeFile) {
        if (
          employeeFile?.url &&
          employeeFile.url.startsWith(DEFAULT_S3_FILE_BUCKET_URL)
        ) {
          formData.append(
            "employee_file_url",
            employeeFile.url.replace(`${DEFAULT_S3_FILE_BUCKET_URL}/`, ""),
          );
        } else {
          formData.append("employee_file", employeeFile);
        }
      }

      // console.log("Form values: ", formData);
      selectedAttributes.forEach((id) => formData.append("attributes", id));
      const headers = { "Content-Type": "multipart/form-data" };
      const { data } = await onSubmit({
        url: "/company/onboarding/employees/",
        method: "POST",
        data: formData,
        headers,
      });
      onUpdateOnboarding(data);
      onChangeStep();
    } catch (error) {
      messageApi.error(
        error?.response?.data?.employee_file || error?.response?.data?.message,
      );
    }
  };

  return (
    <>
      <Wrapper>
        <TitleWrapper>
          <h3>Welcome, {onboarding?.full_name}!</h3>
          <p>
            Welcome aboard! You're just moments away from building a workplace
            culture your team will love. To tailor the experience perfectly to
            your company, we’ll need a few quick details from you. The more you
            share, the smarter our platform becomes in shaping a culture that
            truly thrives.{" "}
          </p>
        </TitleWrapper>
        <StyledCard>
          <Flex align="center" justify="space-between" gap={10}>
            <div style={{ marginBottom: "10px" }}>
              <LabelText>Core Values</LabelText>
              {/* <Tooltip
                placement="topLeft"
                title="These are the key things you want to measure. Many clients list their core values here, plus any other qualities they’d like to assess."
              >
                <QuestionCircleTwoTone
                  twoToneColor={colorPalette.primary[500]}
                  style={{ marginLeft: "8px", fontSize: "16px" }}
                />
              </Tooltip> */}
              <small>
                Core values reflect the behaviors and qualities that matter most
                in your organization. Selecting them helps Elrin measure and
                strengthen your workplace culture.
              </small>
            </div>
            <span
              style={{
                color:
                  selectedAttributes.length > 0
                    ? colorPalette.primary[500]
                    : "rgba(12,12,12,0.88)",
                cursor: selectedAttributes.length > 0 ? "pointer" : "disabled",
                textAlign: "right",
              }}
              onClick={() => {
                if (!selectedAttributes.length) {
                  return;
                }
                setShowAttributesModal(true);
              }}
            >
              Add Definition
            </span>
          </Flex>
          <StyledSelect
            options={attributes}
            loading={attributeLoading}
            showSearch
            value={selectedAttributes}
            mode="tags"
            placeholder="Select core values"
            optionFilterProp="label"
            onChange={setSelectedAttributes}
          />
        </StyledCard>
        <StyledCard>
          <LabelText>Workspace URL</LabelText>
          <Input
            addonAfter=".elrin.ai"
            style={{ width: "30%" }}
            value={slugName}
            onChange={(e) => setSlugName(e.target.value)}
          />
        </StyledCard>
        <StyledCard>
          <div style={{ marginBottom: "10px" }}>
            <LabelText>Upload Logo</LabelText>
            <small>
              Core values reflect the behaviors and qualities that matter most
              in your organization. Selecting them helps Elrin measure and
              strengthen your workplace culture.
            </small>
          </div>
          <Upload.Dragger
            multiple={false}
            accept="image/*"
            beforeUpload={handleLogoChange}
            fileList={logo ? [logo] : []}
            onRemove={() => setLogo(null)}
            showUploadList={{ showPreviewIcon: false, showRemoveIcon: true }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag image file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Only PNG, JPG, and JPEG files are allowed (max 2MB).
            </p>
          </Upload.Dragger>
        </StyledCard>
        <StyledCard>
          <div style={{ marginBottom: "10px" }}>
            <LabelText>Upload HR Policy Document</LabelText>
            <small>
              HR policies give context to employee feedback and engagement data.
              They help Elrin align insights with how your organization already
              operates. If you do not have one yet, you can always add it later.
            </small>
          </div>
          <Upload.Dragger
            multiple={false}
            accept=".pdf,.doc,.docx,.txt"
            beforeUpload={handleFileChange}
            fileList={policyFile ? [policyFile] : []}
            onRemove={() => setPolicyFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Only PDF, DOC, DOCX, and TXT files are allowed (max 5MB).
            </p>
          </Upload.Dragger>
        </StyledCard>
        <StyledCard>
          <Flex
            gap={10}
            align="center"
            justify="space-between"
            style={{ marginBottom: "10px" }}
          >
            <div style={{ marginBottom: "10px" }}>
              <LabelText style={{ margin: "0px" }}>Upload Employees</LabelText>
              {/* <Tooltip
                placement="topLeft"
                title="Add your employees here. You can download the sample format from the “Download Sample Format” button on the right "
              >
                <QuestionCircleTwoTone
                  twoToneColor={colorPalette.primary[500]}
                  style={{ marginLeft: "8px", fontSize: "16px" }}
                />
              </Tooltip> */}
              <small>
                Uploading employee details enables accurate surveys delivery and
                meaningful actions. Get started by downloading the sample format
                and uploading it here.
              </small>
            </div>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 1,
                    label: (
                      <a
                        href="https://elrin-assets.s3.ap-south-1.amazonaws.com/files/elrin_onboarding_sample.xlsx"
                        target="_blank"
                      >
                        XLSX File
                      </a>
                    ),
                  },
                  {
                    key: 2,
                    label: (
                      <a
                        href="https://elrin-assets.s3.ap-south-1.amazonaws.com/files/elrin_onboarding_sample.csv"
                        target="_blank"
                      >
                        CSV File
                      </a>
                    ),
                  },
                ],
              }}
            >
              <Button type="primary" ghost>
                Download Sample Format
              </Button>
            </Dropdown>
          </Flex>
          <Upload.Dragger
            multiple={false}
            beforeUpload={beforeEmployeeUpload}
            showUploadList={true}
            fileList={employeeFile ? [employeeFile] : []}
            accept=".csv,.xls,.xlsx"
            onRemove={() => setEmployeeFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload.
            </p>
            <p className="ant-upload-hint">
              Only CSV or Excel (.xls, .xlsx) files are are allowed.
            </p>
          </Upload.Dragger>
        </StyledCard>
        <ButtonWrapper>
          <Button
            type="primary"
            size="large"
            loading={submitLoading}
            disabled={
              !(
                // policyFile &&
                (employeeFile && selectedAttributes.length && slugName)
              )
            }
            onClick={() => onFinish()}
          >
            Next
          </Button>
        </ButtonWrapper>
      </Wrapper>
      <AttributesDefinitionModal
        onboarding={onboarding}
        open={showAttributesModal}
        onClose={() => setShowAttributesModal(false)}
        attributes={attributes}
        selectedAttributes={selectedAttributes || []}
      />
      {contextHolder}
    </>
  );
};

export default CompanyInfo;
